import type { ActivityEvent, McpClient, McpEnvironment, PublishedCapability } from './governedMcpTypes';

export type DashboardRange = '24h' | '7d' | '30d' | '90d';
export type CapabilityView = 'tool' | 'resource' | 'prompt';
export type SourceKindFilter = 'all' | 'data_model' | 'workflow' | 'knowledge_base' | 'external_mcp';
export type EndpointEnvFilter = 'all' | McpEnvironment;

export interface DashboardFilters {
  endpointEnv: EndpointEnvFilter;
  sourceKind: SourceKindFilter;
  capabilityView: CapabilityView;
}

export interface RequestPoint {
  label: string;
  production: number;
  test: number;
  development: number;
  total: number;
}

export interface CapabilityBar {
  name: string;
  invocations: number;
  successRate: number;
  kind: CapabilityView;
}

export type OutcomeKey = 'successful' | 'blocked' | 'approval' | 'failed' | 'rate_limited';

export interface OutcomeSlice {
  key: OutcomeKey;
  name: string;
  value: number;
  color: string;
}

export interface LatencyPoint {
  label: string;
  p50: number;
  p95: number;
}

export interface GovernancePoint {
  label: string;
  injection: number;
  pii: number;
  secrets: number;
  jailbreak: number;
  output: number;
  policy: number;
}

export interface HeatmapCell {
  client: string;
  group: string;
  value: number;
}

export interface DashboardKpis {
  requests: number;
  requestsDelta: number;
  successRate: number;
  successDelta: number;
  blocked: number;
  blockedDelta: number;
  approvalHeld: number;
  p95: number;
  p95Delta: number;
  avgP50: number;
  liveTools: number;
  sparks: {
    requests: number[];
    success: number[];
    blocked: number[];
    latency: number[];
  };
}

export interface ClientVolume {
  name: string;
  requests: number;
  share: number;
}

export interface GovernanceTotal {
  key: 'injection' | 'pii' | 'secrets' | 'jailbreak' | 'output' | 'policy';
  name: string;
  value: number;
}

export interface DashboardModel {
  kpis: DashboardKpis;
  requestsOverTime: RequestPoint[];
  byCapability: CapabilityBar[];
  outcomes: OutcomeSlice[];
  latency: LatencyPoint[];
  governance: GovernancePoint[];
  governanceTotals: GovernanceTotal[];
  clients: ClientVolume[];
  heatmap: { clients: string[]; groups: string[]; cells: HeatmapCell[] };
}

export function compactCount(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${Math.round(value / 1000)}k`;
  if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

const GROUPS = ['Risk & Exposure', 'Policy Intelligence', 'Claims', 'KYC'] as const;
const DEFAULT_CLIENTS = ['Claude Risk', 'ChatGPT Legal', 'Copilot HR', 'Claims Agent'];

const TOOLS: { name: string; kind: CapabilityView; weight: number }[] = [
  { name: 'Get customer exposure', kind: 'tool', weight: 1.4 },
  { name: 'Search policy terms', kind: 'tool', weight: 1.15 },
  { name: 'Start claims review', kind: 'tool', weight: 0.9 },
  { name: 'Query KYC case', kind: 'tool', weight: 0.7 },
  { name: 'Get limit utilization', kind: 'tool', weight: 0.55 },
  { name: 'Score fraud signal', kind: 'tool', weight: 0.45 },
];

const RESOURCES: { name: string; kind: CapabilityView; weight: number }[] = [
  { name: 'Claims data', kind: 'resource', weight: 1.1 },
  { name: 'Policy terms corpus', kind: 'resource', weight: 0.85 },
  { name: 'KYC case files', kind: 'resource', weight: 0.5 },
  { name: 'Exposure snapshots', kind: 'resource', weight: 0.4 },
];

const PROMPTS: { name: string; kind: CapabilityView; weight: number }[] = [
  { name: 'Risk briefing', kind: 'prompt', weight: 0.95 },
  { name: 'Claims summary', kind: 'prompt', weight: 0.72 },
  { name: 'KYC checklist', kind: 'prompt', weight: 0.48 },
  { name: 'Policy Q&A', kind: 'prompt', weight: 0.64 },
];

function hash(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function unit(seed: string): number {
  return (hash(seed) % 10000) / 10000;
}

function lerp(min: number, max: number, t: number): number {
  return min + (max - min) * t;
}

function bucketCount(range: DashboardRange): number {
  if (range === '24h') return 24;
  if (range === '7d') return 7;
  if (range === '30d') return 30;
  return 13;
}

function formatBucket(range: DashboardRange, index: number, total: number): string {
  const now = new Date();
  if (range === '24h') {
    const hour = new Date(now.getTime() - (total - 1 - index) * 60 * 60 * 1000);
    return `${String(hour.getHours()).padStart(2, '0')}:00`;
  }
  const stepDays = range === '90d' ? 7 : 1;
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (total - 1 - index) * stepDays);
  return day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function volumeScale(range: DashboardRange): number {
  if (range === '24h') return 4.2;
  if (range === '7d') return 26;
  if (range === '30d') return 34;
  return 88;
}

function businessHourFactor(hour: number): number {
  if (hour < 6 || hour >= 22) return 0.18;
  if (hour < 8 || hour >= 19) return 0.42;
  if (hour === 10 || hour === 11 || hour === 14 || hour === 15) return 1.18;
  return 0.92;
}

function weekdayFactor(date: Date): number {
  const day = date.getDay();
  if (day === 0 || day === 6) return 0.38;
  if (day === 1 || day === 5) return 0.88;
  return 1.08;
}

function envMix(env: EndpointEnvFilter): { production: number; test: number; development: number } {
  if (env === 'production') return { production: 1, test: 0, development: 0 };
  if (env === 'test') return { production: 0, test: 1, development: 0 };
  if (env === 'development') return { production: 0, test: 0, development: 1 };
  return { production: 1, test: 1, development: 1 };
}

function sourceLatencyBias(kind: SourceKindFilter): number {
  if (kind === 'workflow') return 1.35;
  if (kind === 'external_mcp') return 1.2;
  if (kind === 'knowledge_base') return 1.1;
  if (kind === 'data_model') return 0.92;
  return 1;
}

function bucketDate(range: DashboardRange, index: number, total: number): Date {
  const now = new Date();
  if (range === '24h') return new Date(now.getTime() - (total - 1 - index) * 60 * 60 * 1000);
  const stepDays = range === '90d' ? 7 : 1;
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - (total - 1 - index) * stepDays);
}

function buildRequests(range: DashboardRange, env: EndpointEnvFilter): RequestPoint[] {
  const n = bucketCount(range);
  const scale = volumeScale(range);
  const mix = envMix(env);
  return Array.from({ length: n }, (_, index) => {
    const when = bucketDate(range, index, n);
    const calendar = range === '24h' ? businessHourFactor(when.getHours()) : weekdayFactor(when);
    const trend = 0.86 + (index / Math.max(1, n - 1)) * 0.22;
    const pulse = 0.9 + 0.1 * Math.sin((index / n) * Math.PI * 2);
    const campaign = index === Math.round(n * 0.62) ? 1.28 : 1;
    const noise = lerp(0.92, 1.08, unit(`req-${range}-${index}`));
    const base = 52 * scale * calendar * trend * pulse * campaign * noise;
    const production = Math.round(base * 0.68 * mix.production);
    const test = Math.round(base * 0.21 * mix.test);
    const development = Math.round(base * 0.11 * mix.development);
    return {
      label: formatBucket(range, index, n),
      production,
      test,
      development,
      total: production + test + development,
    };
  });
}

function capabilityCatalog(view: CapabilityView, published?: PublishedCapability[]): { name: string; kind: CapabilityView; weight: number }[] {
  if (view === 'prompt') return PROMPTS;
  if (published?.length) {
    const rows = published
      .filter((item) => (view === 'resource' ? item.mcpCapability === 'resource' : item.mcpCapability !== 'resource'))
      .slice(0, 6)
      .map((item, index) => ({
        name: (item.mcpToolName || item.sourceName).replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
        kind: view,
        weight: 1.25 - index * 0.12,
      }));
    if (rows.length) return rows;
  }
  return view === 'resource' ? RESOURCES : TOOLS;
}

function buildCapabilityBars(range: DashboardRange, view: CapabilityView, published?: PublishedCapability[]): CapabilityBar[] {
  const catalog = capabilityCatalog(view, published);
  const scale = volumeScale(range) * (range === '24h' ? 7 : range === '7d' ? 16 : range === '30d' ? 38 : 22);
  return catalog
    .map((item) => {
      const invocations = Math.round(scale * item.weight * lerp(0.86, 1.16, unit(`cap-${range}-${item.name}`)));
      const successRate = Math.round(lerp(91, 99.2, unit(`ok-${item.name}`)) * 10) / 10;
      return { name: item.name, invocations, successRate, kind: item.kind };
    })
    .sort((a, b) => b.invocations - a.invocations);
}

function buildOutcomes(range: DashboardRange): OutcomeSlice[] {
  const scale = volumeScale(range) * (range === '24h' ? 38 : range === '7d' ? 96 : range === '30d' ? 210 : 86);
  return [
    { key: 'successful', name: 'Successful', value: Math.round(scale * 0.842), color: '#059669' },
    { key: 'blocked', name: 'Blocked by policy', value: Math.round(scale * 0.071), color: '#e11d48' },
    { key: 'approval', name: 'Approval required', value: Math.round(scale * 0.046), color: '#d97706' },
    { key: 'failed', name: 'Failed', value: Math.round(scale * 0.026), color: '#64748b' },
    { key: 'rate_limited', name: 'Rate limited', value: Math.max(4, Math.round(scale * 0.015)), color: '#4f46e5' },
  ];
}

function buildLatency(range: DashboardRange, env: EndpointEnvFilter, source: SourceKindFilter): LatencyPoint[] {
  const n = bucketCount(range);
  const bias = sourceLatencyBias(source) * (env === 'production' ? 0.92 : env === 'test' ? 1.06 : env === 'development' ? 1.14 : 1);
  let walk = 168;
  return Array.from({ length: n }, (_, index) => {
    const when = bucketDate(range, index, n);
    const load = range === '24h' ? businessHourFactor(when.getHours()) : weekdayFactor(when);
    const incident = index === Math.round(n * 0.71) ? 1.55 : 1;
    walk = walk * 0.72 + lerp(150, 205, unit(`p50-${range}-${index}`)) * 0.28;
    const p50 = Math.round(walk * bias * (0.82 + load * 0.22) * incident);
    const p95 = Math.round(p50 * lerp(1.85, 2.2, unit(`p95-${range}-${index}`)) * (incident > 1 ? 1.12 : 1));
    return { label: formatBucket(range, index, n), p50, p95 };
  });
}

function buildGovernance(range: DashboardRange): GovernancePoint[] {
  const n = bucketCount(range);
  const scale = range === '24h' ? 1.15 : range === '7d' ? 2.8 : range === '30d' ? 2.4 : 3.4;
  return Array.from({ length: n }, (_, index) => {
    const when = bucketDate(range, index, n);
    const quiet = range === '24h' ? (when.getHours() < 7 || when.getHours() > 20 ? 0.35 : 1) : weekdayFactor(when);
    const cluster = index === Math.round(n * 0.38) || index === Math.round(n * 0.74) ? 2.1 : 1;
    const seed = `gov-${range}-${index}`;
    const intensity = scale * quiet * cluster;
    return {
      label: formatBucket(range, index, n),
      injection: Math.round(lerp(0, 5, unit(`${seed}-inj`)) * intensity),
      pii: Math.round(lerp(1, 9, unit(`${seed}-pii`)) * intensity),
      secrets: Math.round(lerp(0, 3, unit(`${seed}-sec`)) * intensity),
      jailbreak: Math.round(lerp(0, 2.4, unit(`${seed}-jail`)) * intensity),
      output: Math.round(lerp(0, 6, unit(`${seed}-out`)) * intensity),
      policy: Math.round(lerp(1, 7, unit(`${seed}-pol`)) * intensity),
    };
  });
}

function heatmapScale(range: DashboardRange): number {
  if (range === '24h') return 0.55;
  if (range === '7d') return 1.15;
  if (range === '30d') return 2.6;
  return 4.1;
}

function shortClientName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('claude')) return 'Claude Risk';
  if (lower.includes('chatgpt')) return 'ChatGPT Legal';
  if (lower.includes('copilot')) return 'Copilot HR';
  if (lower.includes('claims')) return 'Claims Agent';
  const trimmed = name.replace(/\s+[—-].*$/, '').trim();
  return trimmed.length > 22 ? `${trimmed.slice(0, 20)}…` : trimmed || name;
}

function resolveHeatmapClients(live?: McpClient[]): string[] {
  const fromLive = (live || []).map((client) => shortClientName(client.name));
  const unique: string[] = [];
  fromLive.concat(DEFAULT_CLIENTS).forEach((name) => {
    if (!unique.includes(name)) unique.push(name);
  });
  return unique.slice(0, 4);
}

function groupAffinity(client: string, group: string): number {
  const lower = client.toLowerCase();
  if (lower.includes('claude') && group.startsWith('Risk')) return 1.85;
  if (lower.includes('claude') && group.startsWith('Policy')) return 1.45;
  if (lower.includes('chatgpt') && group.startsWith('Policy')) return 1.7;
  if (lower.includes('chatgpt') && group === 'KYC') return 1.25;
  if (lower.includes('copilot') && group === 'Claims') return 0.55;
  if (lower.includes('copilot')) return 0.7;
  if (lower.includes('claims') && group === 'Claims') return 1.9;
  if (lower.includes('claims') && group === 'KYC') return 1.2;
  return 0.85;
}

function buildHeatmap(clients: string[], range: DashboardRange): { clients: string[]; groups: string[]; cells: HeatmapCell[] } {
  const rows = clients.length ? clients : DEFAULT_CLIENTS;
  const scale = heatmapScale(range);
  const cells: HeatmapCell[] = [];
  rows.forEach((client) => {
    GROUPS.forEach((group) => {
      cells.push({
        client,
        group,
        value: Math.round(lerp(6, 88, unit(`${client}-${group}-${range}`)) * groupAffinity(client, group) * scale),
      });
    });
  });
  return { clients: rows, groups: [...GROUPS], cells };
}

function overlayLiveKpis(
  base: DashboardKpis,
  activity: ActivityEvent[] | undefined,
  published: PublishedCapability[] | undefined
): DashboardKpis {
  const liveTools = published?.filter((item) => item.status === 'published').length ?? base.liveTools;
  if (!activity?.length) return { ...base, liveTools };
  const requests = activity.length;
  const allowed = activity.filter((item) => item.decision === 'allowed').length;
  const blocked = activity.filter((item) => item.decision === 'denied').length;
  const approvalHeld = activity.filter((item) => item.decision === 'approval_required').length;
  const durations = [...activity.map((item) => item.durationMs)].sort((a, b) => a - b);
  const p95 = durations.length ? durations[Math.min(durations.length - 1, Math.floor(durations.length * 0.95))] : base.p95;
  return {
    ...base,
    requests: base.requests + requests,
    successRate: Math.round(((allowed + base.requests * (base.successRate / 100)) / (base.requests + requests)) * 1000) / 10,
    blocked: base.blocked + blocked,
    approvalHeld: base.approvalHeld + approvalHeld,
    p95: Math.round((base.p95 + p95) / 2),
    liveTools,
  };
}

function periodDelta(values: number[]): number {
  if (values.length < 4) return 0;
  const mid = Math.floor(values.length / 2);
  const prior = values.slice(0, mid).reduce((sum, value) => sum + value, 0) / mid;
  const current = values.slice(mid).reduce((sum, value) => sum + value, 0) / (values.length - mid);
  if (!prior) return 0;
  return Math.round(((current - prior) / prior) * 100);
}

export const DEFAULT_FILTERS: DashboardFilters = {
  endpointEnv: 'all',
  sourceKind: 'all',
  capabilityView: 'tool',
};

export function buildDashboardModel(
  range: DashboardRange,
  filters: DashboardFilters,
  store?: {
    activity?: ActivityEvent[];
    published?: PublishedCapability[];
    clients?: McpClient[];
  }
): DashboardModel {
  const requestsOverTime = buildRequests(range, filters.endpointEnv);
  const byCapability = buildCapabilityBars(range, filters.capabilityView, store?.published);
  const outcomes = buildOutcomes(range);
  const latency = buildLatency(range, filters.endpointEnv, filters.sourceKind);
  const governance = buildGovernance(range);
  const heatmap = buildHeatmap(resolveHeatmapClients(store?.clients), range);

  const requestTotals = requestsOverTime.map((point) => point.total);
  const requestTotal = requestTotals.reduce((sum, value) => sum + value, 0);
  const outcomeTotal = outcomes.reduce((sum, item) => sum + item.value, 0) || 1;
  const successful = outcomes.find((item) => item.key === 'successful')?.value || 0;
  const blocked = outcomes.find((item) => item.key === 'blocked')?.value || 0;
  const approvalHeld = outcomes.find((item) => item.key === 'approval')?.value || 0;
  const rankedP95 = latency.map((point) => point.p95).sort((left, right) => left - right);
  const p95 = rankedP95.length ? rankedP95[Math.min(rankedP95.length - 1, Math.floor(rankedP95.length * 0.95))] : 0;
  const avgP50 = latency.length ? Math.round(latency.reduce((sum, point) => sum + point.p50, 0) / latency.length) : 0;
  const liveTools = store?.published
    ? store.published.filter((item) => item.status === 'published').length
    : TOOLS.length;
  const successSpark = requestsOverTime.map((point) => Math.round(point.total * (successful / outcomeTotal)));
  const blockedSpark = requestsOverTime.map((point) => Math.max(0, Math.round(point.total * (blocked / outcomeTotal))));
  const latencySpark = latency.map((point) => point.p95);

  const governanceSeed: GovernanceTotal[] = [
    { key: 'pii', name: 'PII', value: 0 },
    { key: 'policy', name: 'Policy denial', value: 0 },
    { key: 'output', name: 'Output', value: 0 },
    { key: 'injection', name: 'Prompt injection', value: 0 },
    { key: 'secrets', name: 'Secrets', value: 0 },
    { key: 'jailbreak', name: 'Jailbreak', value: 0 },
  ];
  const governanceTotals = governanceSeed
    .map((row) => ({
      key: row.key,
      name: row.name,
      value: governance.reduce((sum, point) => sum + point[row.key], 0),
    }))
    .sort((left, right) => right.value - left.value);

  const clientRows = heatmap.clients
    .map((name) => ({
      name,
      requests: heatmap.cells.filter((cell) => cell.client === name).reduce((sum, cell) => sum + cell.value, 0),
    }))
    .sort((left, right) => right.requests - left.requests);
  const clientSum = clientRows.reduce((sum, row) => sum + row.requests, 0) || 1;
  const clients: ClientVolume[] = clientRows.map((row) => ({
    ...row,
    share: Math.round((row.requests / clientSum) * 100),
  }));

  const kpis = overlayLiveKpis(
    {
      requests: requestTotal,
      requestsDelta: periodDelta(requestTotals),
      successRate: Math.round((successful / outcomeTotal) * 1000) / 10,
      successDelta: periodDelta(successSpark.map((value, index) => (requestTotals[index] ? (value / requestTotals[index]) * 100 : 0))),
      blocked,
      blockedDelta: periodDelta(blockedSpark),
      approvalHeld,
      p95,
      p95Delta: periodDelta(latencySpark),
      avgP50,
      liveTools,
      sparks: {
        requests: requestTotals,
        success: successSpark,
        blocked: blockedSpark,
        latency: latencySpark,
      },
    },
    store?.activity,
    store?.published
  );

  return {
    kpis,
    requestsOverTime,
    byCapability,
    outcomes,
    latency,
    governance,
    governanceTotals,
    clients,
    heatmap,
  };
}
